import { useEffect, useMemo, useState } from "react";
import { FileQuestion, LoaderCircle, Music2 } from "lucide-react";
import type { AssetRecord } from "@focalfolio/domain-model";
import { recipeToCssFilter } from "@focalfolio/application-core";
import { localPreviewUrl, sourceObjectUrl } from "../lib/runtime";

interface PreviewSurfaceProps {
  asset: AssetRecord;
  className?: string;
  fit?: "contain" | "cover";
  controls?: boolean;
  caption?: boolean;
}

export function PreviewSurface({ asset, className = "", fit = "contain", controls = true, caption = false }: PreviewSurfaceProps) {
  const [url, setUrl] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const direct = asset.kind === "video" || asset.kind === "audio" || asset.kind === "pdf";

  useEffect(() => {
    let cancelled = false;
    let disposable: string | undefined;
    setLoading(true);
    setError(undefined);
    void (async () => {
      try {
        const next = direct ? await sourceObjectUrl(asset) : await localPreviewUrl(asset);
        if (cancelled) {
          if (direct && next) URL.revokeObjectURL(next);
          return;
        }
        disposable = direct ? next : undefined;
        setUrl(next);
      } catch (reason) {
        if (!cancelled) setError(reason instanceof Error ? reason.message : String(reason));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (disposable) URL.revokeObjectURL(disposable);
    };
  }, [asset.id, asset.fingerprint, direct]);

  const imageStyle = useMemo(
    () => ({ objectFit: fit, filter: asset.adjustment ? recipeToCssFilter(asset.adjustment) : undefined, transform: asset.adjustment?.rotation ? `rotate(${asset.adjustment.rotation}deg)` : undefined }),
    [asset.adjustment, fit]
  );

  if (loading) return <div className={`preview-status ${className}`}><LoaderCircle className="spin" size={24} /><span>Rendering locally</span></div>;
  if (error || !url) return <div className={`preview-status preview-error ${className}`}><FileQuestion size={30} /><span>{error ?? "No embedded preview is available for this format."}</span></div>;

  return <figure className={`preview-surface ${className}`}>
    {asset.kind === "video" ? <video src={url} controls={controls} playsInline preload="metadata" /> : null}
    {asset.kind === "audio" ? <div className="audio-preview"><Music2 size={54} /><audio src={url} controls={controls} preload="metadata" /></div> : null}
    {asset.kind === "pdf" ? <object data={url} type="application/pdf" aria-label={`PDF preview of ${asset.filename}`}><a href={url}>Open PDF</a></object> : null}
    {!["video", "audio", "pdf"].includes(asset.kind) ? <img src={url} alt={asset.metadata.title || asset.filename} style={imageStyle} draggable={false} /> : null}
    {caption ? <figcaption>{asset.filename}</figcaption> : null}
  </figure>;
}

export function Thumbnail({ asset }: { asset: AssetRecord }) {
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    setFailed(false);
    if (["video", "audio", "pdf", "document", "other"].includes(asset.kind)) return () => { live = false; };
    void localPreviewUrl(asset).then((next) => { if (live) setUrl(next); }).catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [asset.id, asset.fingerprint]);
  if (url && !failed) return <img className="asset-thumbnail" src={url} alt="" loading="lazy" draggable={false} style={{ filter: asset.adjustment ? recipeToCssFilter(asset.adjustment) : undefined }} />;
  return <div className={`thumbnail-placeholder kind-${asset.kind}`}><span>{asset.kind === "raw" ? "RAW" : asset.extension.toUpperCase() || asset.kind.toUpperCase()}</span></div>;
}
